<template>
  <div class="macd-optimize">
    <!-- 页面标题 -->
    <div class="page-header">
      <h1 class="page-title">
        <i class="el-icon-data-analysis"></i>
        MACD参数优化
      </h1>
      <p class="page-subtitle">MACD策略参数自动寻优</p>
    </div>

    <!-- 参数配置 -->
    <el-card class="config-card" shadow="never">
      <el-form :model="searchForm" label-width="140px" class="search-form">
        <el-row :gutter="20">
          <!-- 股票选择 -->
          <el-col :span="6">
            <el-form-item label="股票代码">
              <el-select
                v-model="searchForm.code"
                filterable
                placeholder="选择股票"
                @change="handleCodeChange"
                style="width: 100%"
              >
                <el-option
                  v-for="stock in stockList"
                  :key="stock.code"
                  :label="`${stock.code} - ${stock.name}`"
                  :value="stock.code"
                />
              </el-select>
            </el-form-item>
          </el-col>

          <!-- 日期范围 -->
          <el-col :span="6">
            <el-form-item label="开始日期">
              <el-date-picker
                v-model="searchForm.startDate"
                type="date"
                placeholder="开始日期"
                value-format="yyyy-MM-dd"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>

          <el-col :span="6">
            <el-form-item label="结束日期">
              <el-date-picker
                v-model="searchForm.endDate"
                type="date"
                placeholder="结束日期"
                value-format="yyyy-MM-dd"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-divider content-position="left">优化参数范围</el-divider>

        <!-- 优化参数 -->
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="快线周期列表">
              <el-input
                v-model="searchForm.periodMe1List"
                placeholder="如: 8,12,16"
                size="small"
              />
            </el-form-item>
          </el-col>

          <el-col :span="8">
            <el-form-item label="慢线周期列表">
              <el-input
                v-model="searchForm.periodMe2List"
                placeholder="如: 20,26,34"
                size="small"
              />
            </el-form-item>
          </el-col>

          <el-col :span="8">
            <el-form-item label="信号线周期列表">
              <el-input
                v-model="searchForm.periodSignalList"
                placeholder="如: 6,9,12"
                size="small"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <!-- 止损比例 -->
          <el-col :span="8">
            <el-form-item label="止损比例">
              <el-input-number
                v-model="searchForm.stopLoss"
                :min="0.01"
                :max="0.5"
                :step="0.01"
                :precision="2"
                size="small"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>

          <!-- 移动止损 -->
          <el-col :span="8">
            <el-form-item label="移动止损">
              <el-input-number
                v-model="searchForm.trailingStop"
                :min="0.01"
                :max="0.3"
                :step="0.01"
                :precision="2"
                size="small"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row>
          <el-col :span="24" style="text-align: center">
            <el-button
              type="primary"
              :loading="loading"
              @click="runOptimize"
              size="medium"
              icon="el-icon-data-analysis"
            >
              {{ loading ? '优化中...' : '开始优化' }}
            </el-button>
            <el-alert
              v-if="!loading && optimizeResult"
              :title="`共测试 ${optimizeResult.total_combinations} 组参数`"
              type="info"
              :closable="false"
              style="margin-left: 20px; display: inline-block; vertical-align: middle"
            />
          </el-col>
        </el-row>
      </el-form>
    </el-card>

    <!-- 优化结果 -->
    <div v-if="optimizeResult" class="results-section">
      <!-- 最佳参数卡片 -->
      <el-card class="best-params-card" shadow="hover">
        <div slot="header" class="card-header">
          <i class="el-icon-trophy"></i>
          <span>最佳参数组合</span>
        </div>
        <el-row :gutter="20" class="best-params-content">
          <el-col :span="8">
            <div class="param-item">
              <span class="label">快线周期</span>
              <span class="value">{{ optimizeResult.best_params.period_me1 }}</span>
            </div>
          </el-col>
          <el-col :span="8">
            <div class="param-item">
              <span class="label">慢线周期</span>
              <span class="value">{{ optimizeResult.best_params.period_me2 }}</span>
            </div>
          </el-col>
          <el-col :span="8">
            <div class="param-item">
              <span class="label">信号线周期</span>
              <span class="value">{{ optimizeResult.best_params.period_signal }}</span>
            </div>
          </el-col>
        </el-row>
        <el-divider />
        <el-row :gutter="20" class="performance-content">
          <el-col :span="4">
            <div class="param-item">
              <span class="label">最终资金</span>
              <span class="value value-success">
                ¥{{ optimizeResult.best_params.final_value?.toFixed(2) || '-' }}
              </span>
            </div>
          </el-col>
          <el-col :span="4">
            <div class="param-item">
              <span class="label">总收益</span>
              <span class="value" :class="optimizeResult.best_params.total_profit >= 0 ? 'value-success' : 'value-danger'">
                ¥{{ optimizeResult.best_params.total_profit?.toFixed(2) || '-' }}
              </span>
            </div>
          </el-col>
          <el-col :span="4">
            <div class="param-item">
              <span class="label">总佣金</span>
              <span class="value value-warning">
                -¥{{ optimizeResult.best_params.total_commission?.toFixed(2) || '0.00' }}
              </span>
            </div>
          </el-col>
          <el-col :span="4">
            <div class="param-item">
              <span class="label">滑点损失</span>
              <span class="value value-danger">
                -¥{{ optimizeResult.best_params.total_slippage?.toFixed(2) || '0.00' }}
              </span>
            </div>
          </el-col>
          <el-col :span="4">
            <div class="param-item">
              <span class="label">收益率</span>
              <span class="value" :class="optimizeResult.best_params.roi >= 0 ? 'value-success' : 'value-danger'">
                {{ optimizeResult.best_params.roi?.toFixed(3) || '-' }}%
              </span>
            </div>
          </el-col>
          <el-col :span="4">
            <div class="param-item">
              <span class="label">胜率</span>
              <span class="value">
                {{ optimizeResult.best_params.win_rate?.toFixed(2) || '-' }}%
              </span>
            </div>
          </el-col>
        </el-row>
      </el-card>

      <!-- 所有结果表格 -->
      <el-card class="results-table-card" shadow="never">
        <div slot="header" class="card-header">
          <i class="el-icon-s-data"></i>
          <span>所有参数组合结果</span>
          <el-button
            type="text"
            icon="el-icon-download"
            @click="exportResults"
            size="small"
          >
            导出结果
          </el-button>
        </div>
        <el-table
          :data="optimizeResult.all_results"
          stripe
          size="small"
          :row-class-name="tableRowClassName"
          max-height="500"
          class="results-table"
        >
          <el-table-column type="index" label="排名" width="70" align="center" />
          <el-table-column prop="period_me1" label="快线" width="85" align="center" />
          <el-table-column prop="period_me2" label="慢线" width="85" align="center" />
          <el-table-column prop="period_signal" label="信号线" width="90" align="center" />
          <el-table-column label="最终资金" width="140" align="right" sortable>
            <template slot-scope="scope">
              <span :class="scope.row.final_value >= 100000 ? 'value-success' : 'value-danger'">
                ¥{{ scope.row.final_value?.toFixed(2) || '-' }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="总收益" width="120" align="right" sortable>
            <template slot-scope="scope">
              <span :class="scope.row.total_profit >= 0 ? 'value-success' : 'value-danger'">
                ¥{{ scope.row.total_profit?.toFixed(2) || '-' }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="总佣金" width="100" align="right" sortable>
            <template slot-scope="scope">
              <span class="value-warning">
                -¥{{ scope.row.total_commission?.toFixed(2) || '0.00' }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="滑点损失" width="110" align="right" sortable>
            <template slot-scope="scope">
              <span class="value-danger">
                -¥{{ scope.row.total_slippage?.toFixed(2) || '0.00' }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="收益率" width="100" align="right" sortable>
            <template slot-scope="scope">
              <span :class="scope.row.roi >= 0 ? 'value-success' : 'value-danger'">
                {{ scope.row.roi?.toFixed(3) || '-' }}%
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="total_orders" label="订单数" width="90" align="center" sortable />
          <el-table-column prop="total_trades" label="完整交易" width="110" align="center" sortable />
          <el-table-column label="盈利" width="80" align="center">
            <template slot-scope="scope">
              <span class="value-success">{{ scope.row.won_trades }}</span>
            </template>
          </el-table-column>
          <el-table-column label="亏损" width="80" align="center">
            <template slot-scope="scope">
              <span class="value-danger">{{ scope.row.lost_trades }}</span>
            </template>
          </el-table-column>
          <el-table-column label="胜率" width="100" align="right" sortable>
            <template slot-scope="scope">
              <span :class="getWinRateClass(scope.row.win_rate)">
                {{ scope.row.win_rate?.toFixed(2) || '-' }}%
              </span>
            </template>
          </el-table-column>
        </el-table>
      </el-card>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'BacktestMACDOptimize',
  data() {
    return {
      stockList: [],
      searchForm: {
        code: 'sz.002959',
        startDate: '2023-01-01',
        endDate: new Date().toISOString().split('T')[0],
        periodMe1List: '8,12,16',
        periodMe2List: '20,26,34',
        periodSignalList: '6,9,12',
        stopLoss: 0.05,
        trailingStop: 0.03
      },
      loading: false,
      optimizeResult: null
    }
  },
  watch: {
    '$route.query.code': {
      immediate: true,
      handler(newCode) {
        if (newCode && newCode !== this.searchForm.code) {
          this.searchForm.code = newCode
          localStorage.setItem('macd_optimize_stock_code', newCode)
        }
      }
    },
    'searchForm.code'(newCode) {
      if (newCode) {
        localStorage.setItem('macd_optimize_stock_code', newCode)
        if (this.$route.query.code !== newCode) {
          this.$router.replace({ query: { code: newCode } }).catch(() => {})
        }
      }
    }
  },
  mounted() {
    this.loadStockList()
    this.restoreSelectedCode()
  },
  methods: {
    async loadStockList() {
      try {
        const response = await axios.get('/stock/info/list')
        if (response.data.status === 'success') {
          this.stockList = response.data.data
        }
      } catch (error) {
        console.error('加载股票列表失败:', error)
      }
    },

    restoreSelectedCode() {
      const urlCode = this.$route.query.code
      const savedCode = localStorage.getItem('macd_optimize_stock_code')
      const code = urlCode || savedCode

      if (code) {
        this.searchForm.code = code
        localStorage.setItem('macd_optimize_stock_code', code)
      }
    },

    handleCodeChange(code) {
      localStorage.setItem('macd_optimize_stock_code', code)
    },

    async runOptimize() {
      if (!this.searchForm.code) {
        this.$message.warning('请选择股票')
        return
      }

      // 解析参数
      const periodMe1Array = this.searchForm.periodMe1List.split(',').map(s => parseInt(s.trim()))
      const periodMe2Array = this.searchForm.periodMe2List.split(',').map(s => parseInt(s.trim()))
      const periodSignalArray = this.searchForm.periodSignalList.split(',').map(s => parseInt(s.trim()))

      // 验证参数
      if (periodMe1Array.some(isNaN) || periodMe2Array.some(isNaN) || periodSignalArray.some(isNaN)) {
        this.$message.warning('参数格式不正确，请检查')
        return
      }

      if (periodMe1Array.length === 0 || periodMe2Array.length === 0 || periodSignalArray.length === 0) {
        this.$message.warning('参数不能为空')
        return
      }

      // 计算组合数量
      const totalCombos = periodMe1Array.length * periodMe2Array.length * periodSignalArray.length

      if (totalCombos > 500) {
        this.$confirm(
          `将测试 ${totalCombos} 组参数，这可能需要较长时间。是否继续？`,
          '确认优化',
          {
            confirmButtonText: '继续',
            cancelButtonText: '取消',
            type: 'warning'
          }
        ).then(() => {
          this.executeOptimize()
        }).catch(() => {
          this.$message.info('已取消优化')
        })
      } else {
        this.executeOptimize()
      }
    },

    async executeOptimize() {
      this.loading = true
      try {
        const params = {
          code: this.searchForm.code,
          start_date: this.searchForm.startDate,
          end_date: this.searchForm.endDate,
          period_me1_list: this.searchForm.periodMe1List,
          period_me2_list: this.searchForm.periodMe2List,
          period_signal_list: this.searchForm.periodSignalList,
          stop_loss_pct: this.searchForm.stopLoss,
          trailing_stop: this.searchForm.trailingStop
        }

        const response = await axios.get('/backtest/macd-optimize', { params })

        if (response.data.status === 'success') {
          this.optimizeResult = response.data.data
          this.$message.success(`优化完成！共测试 ${this.optimizeResult.total_combinations} 组参数`)
        } else {
          this.$message.error(response.data.message || '优化失败')
        }
      } catch (error) {
        console.error('优化失败:', error)
        this.$message.error('优化失败: ' + (error.response?.data?.message || error.message))
      } finally {
        this.loading = false
      }
    },

    tableRowClassName({ rowIndex }) {
      if (rowIndex === 0) {
        return 'best-row'
      }
      return ''
    },

    getWinRateClass(winRate) {
      if (winRate >= 60) return 'value-success'
      if (winRate >= 40) return 'value-warning'
      return 'value-danger'
    },

    exportResults() {
      if (!this.optimizeResult) return

      const headers = ['排名', '快线周期', '慢线周期', '信号线周期', '最终资金', '总收益', '总佣金', '滑点损失', '收益率', '订单数', '完整交易', '盈利', '亏损', '胜率']
      const rows = this.optimizeResult.all_results.map((r, i) => [
        i + 1,
        r.period_me1,
        r.period_me2,
        r.period_signal,
        r.final_value?.toFixed(2),
        r.total_profit?.toFixed(2),
        r.total_commission?.toFixed(2),
        r.total_slippage?.toFixed(2),
        r.roi?.toFixed(3) + '%',
        r.total_orders,
        r.total_trades,
        r.won_trades,
        r.lost_trades,
        r.win_rate?.toFixed(2) + '%'
      ])

      let csv = headers.join(',') + '\n'
      rows.forEach(row => {
        csv += row.join(',') + '\n'
      })

      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `macd_optimize_${this.searchForm.code}_${new Date().getTime()}.csv`
      link.click()
      URL.revokeObjectURL(url)

      this.$message.success('导出成功')
    }
  }
}
</script>

<style scoped>
.macd-optimize {
  padding: 24px;
  min-height: calc(100vh - 64px);
}

/* 页面标题 */
.page-header {
  text-align: center;
  margin-bottom: 24px;
}

.page-title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.page-subtitle {
  margin: 8px 0 0;
  font-size: 14px;
  color: #64748b;
}

/* 配置卡片 */
.config-card {
  margin-bottom: 20px;
  background: rgba(30, 41, 59, 0.6);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(99, 102, 241, 0.15);
}

.config-card :deep(.el-card__header) {
  border-bottom: 1px solid rgba(99, 102, 241, 0.15);
  background: rgba(15, 23, 42, 0.5);
}

.config-card :deep(.el-card__body) {
  padding: 20px;
}

.search-form :deep(.el-form-item__label) {
  color: #cbd5e1;
}

/* 结果区域 */
.results-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* 最佳参数卡片 */
.best-params-card {
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(99, 102, 241, 0.3);
}

.best-params-card :deep(.el-card__header) {
  background: rgba(99, 102, 241, 0.15);
  border-bottom: 1px solid rgba(99, 102, 241, 0.2);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #a5b4fc;
}

.best-params-content .param-item,
.performance-content .param-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
}

.param-item .label {
  color: #94a3b8;
  font-size: 14px;
}

.param-item .value {
  font-size: 18px;
  font-weight: 600;
  color: #e2e8f0;
}

/* 结果表格卡片 */
.results-table-card {
  background: rgba(30, 41, 59, 0.6);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(99, 102, 241, 0.15);
}

.results-table-card :deep(.el-card__header) {
  border-bottom: 1px solid rgba(99, 102, 241, 0.15);
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.results-table {
  background: transparent;
  width: 100%;
}

.results-table :deep(.el-table__body-wrapper) {
  width: 100%;
}

.results-table :deep(table) {
  width: 100% !important;
}

.results-table :deep(.el-table__header-wrapper) {
  background: rgba(15, 23, 42, 0.5);
}

.results-table :deep(th.el-table__cell) {
  background: rgba(15, 23, 42, 0.8) !important;
  color: #a5b4fc;
  font-weight: 600;
  border-bottom: 1px solid rgba(99, 102, 241, 0.15);
}

.results-table :deep(td.el-table__cell) {
  border-bottom: 1px solid rgba(99, 102, 241, 0.1);
}

.results-table :deep(.el-table__row) {
  background: rgba(30, 41, 59, 0.3);
}

.results-table :deep(.el-table__row:hover td) {
  background: rgba(99, 102, 241, 0.1) !important;
}

.results-table :deep(.best-row) {
  background: rgba(99, 102, 241, 0.2) !important;
}

.results-table :deep(.best-row td) {
  font-weight: 600;
  color: #a5b4fc !important;
}

/* 值颜色 */
.value-success {
  color: #22c55e;
}

.value-danger {
  color: #ef4444;
}

.value-warning {
  color: #f59e0b;
}

/* 输入框样式 */
.search-form :deep(.el-input__inner),
.search-form :deep(.el-input-number .el-input__inner) {
  background: rgba(15, 23, 42, 0.6) !important;
  border: 1px solid rgba(99, 102, 241, 0.2) !important;
  color: #e2e8f0 !important;
}

.search-form :deep(.el-input__inner):hover,
.search-form :deep(.el-input-number .el-input__inner):hover {
  border-color: rgba(99, 102, 241, 0.4) !important;
}

.search-form :deep(.el-input__inner):focus,
.search-form :deep(.el-input-number .el-input__inner):focus {
  border-color: #6366f1 !important;
}

.search-form :deep(.el-input-number__decrease),
.search-form :deep(.el-input-number__increase) {
  background: rgba(15, 23, 42, 0.8) !important;
  border: 1px solid rgba(99, 102, 241, 0.2) !important;
  color: #a5b4fc !important;
}

.search-form :deep(.el-input-number__decrease:hover),
.search-form :deep(.el-input-number__increase:hover) {
  color: #818cf8 !important;
}

.search-form :deep(.el-date-editor) {
  width: 100%;
}

.search-form :deep(.el-date-editor .el-input__inner) {
  background: rgba(15, 23, 42, 0.6) !important;
  border: 1px solid rgba(99, 102, 241, 0.2) !important;
  color: #e2e8f0 !important;
}

/* 选择器样式 */
.search-form :deep(.el-select-dropdown) {
  background: rgba(15, 23, 42, 0.95) !important;
  border: 1px solid rgba(99, 102, 241, 0.2) !important;
}

.search-form :deep(.el-select-dropdown__item) {
  color: #e2e8f0 !important;
}

.search-form :deep(.el-select-dropdown__item:hover) {
  background: rgba(99, 102, 241, 0.15) !important;
}

.search-form :deep(.el-select-dropdown__item.selected) {
  color: #a5b4fc !important;
  background: rgba(99, 102, 241, 0.2) !important;
}
</style>
